// src/pages/billing/LedgerPage.tsx

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useBilling, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';

function formatMoney(money: any): string {
  if (!money) return '--';
  const amount = (money.amount ?? 0) / Math.pow(10, money.decimal_places ?? 2);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

export const LedgerPage = observer(() => {
  const store = useBilling();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accountId, setAccountId] = useState(searchParams.get('account') || '');
  const [entries, setEntries] = useState<any[]>([]);

  useEffect(() => {
    const account = searchParams.get('account') || '';
    store.ListLedgerEntries().then((res) => {
      const list = res?.ledger_entries?.entries;
      const all = Array.isArray(list) ? list : [];
      setEntries(account ? all.filter((e: any) => e.account?.id === account) : all);
    }).catch(() => {});
  }, [searchParams]);

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(accountId ? { account: accountId } : {});
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">General Ledger</h1>
        <nav className="text-sm">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">General Ledger</span>
        </nav>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleFilter} className="flex gap-4">
            <Input placeholder="Filter by Account ID" value={accountId} onChange={(e) => setAccountId(e.target.value)} fullWidth={false} className="flex-1" />
            <Button type="submit" variant="secondary">Filter</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Journal Entry</TableHead>
              <TableHead className="text-right">Debit</TableHead>
              <TableHead className="text-right">Credit</TableHead>
              <TableHead className="text-right">Running Balance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : entries.length === 0 ? (
              <TableEmpty colSpan={6}>
                <span className="table-empty-message">No ledger entries found</span>
                <p className="mt-1 text-sm text-muted-foreground">Permanent record of all posted journal entries</p>
              </TableEmpty>
            ) : (
              entries.map((item: any) => (
                <TableRow key={item.entity?.id || Math.random()}>
                  <TableCell>{formatDate(item.entry_date || item.posted_at)}</TableCell>
                  <TableCell className="font-mono">{String(item.account?.id ?? '--')}</TableCell>
                  <TableCell>
                    <Link to={`/billing/journal-entries/${item.journal_entry?.id}`} className="text-primary hover:underline">
                      {String(item.journal_entry?.id ?? '--').substring(0, 8)}...
                    </Link>
                  </TableCell>
                  <TableCell className="text-right">{item.debit?.amount ? formatMoney(item.debit) : ''}</TableCell>
                  <TableCell className="text-right">{item.credit?.amount ? formatMoney(item.credit) : ''}</TableCell>
                  <TableCell className="text-right font-medium">{formatMoney(item.running_balance)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default LedgerPage;
